﻿import React, { MouseEvent, useState } from 'react';
import BaseTab from './BaseTab';
import BaseMenuItem from './BaseMenuItem';
import CreateGenericOrderModal from '../../../../../../features/orders/view/generic/CreateGenericOrderModal';
import Divider from '@mui/material/Divider';
import history from '../../../../../../shared/history';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as Features from '../../../../../../shared/Features';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../shared/LanguageService';
import { TranslatedLabels } from '../../../../../../shared/types';
import { useUserContext } from '../../../../../../shared/context/user-context';

type AdminOrderTabProps = {
    label: string;
};

type ExternalProps = {
    anchorElement: HTMLButtonElement | null;
    open: boolean;
    onClose: () => void;
    onClickCreateGenericOrder: () => void;
};

type AdminOrderMenuProps = {
    labels: TranslatedLabels;
} & ExternalProps;

const StyledMenuItem = styled(MenuItem)({
    fontSize: '16px'
});

const PlanListRoute = '/orders-list/plans/';
const PhotoListRoute = '/orders-list/photos/';
const FilmListRoute = '/orders-list/films';
const DigitalStylingListRoute = '/orders-list/digital-styling';
const BookingsListRoute = '/orders-list/bookings';
const AdvancedSearchRoute = '/orders-list/advance';

let routes = [
    PlanListRoute,
    PhotoListRoute,
    FilmListRoute,
    DigitalStylingListRoute,
    BookingsListRoute,
    AdvancedSearchRoute
];

function AdminOrderMenu({
    anchorElement,
    open,
    onClose,
    labels,
    onClickCreateGenericOrder
}: AdminOrderMenuProps) {
    const { user } = useUserContext();

    const shouldShowPlanOrders = Features.hasFeature(Features.ListPlanOrders, user);
    const shouldShowPhotoOrders = Features.hasFeature(Features.ListPhotoOrders, user);
    const shouldShowFilmOrders = Features.hasFeature(Features.ListFilmOrders, user);
    let shouldShowDigitalStylingOrders = Features.hasFeature(
        Features.ListDigitalStylingOrders,
        user
    );
    const shouldShowBookingOrders = Features.hasFeature(Features.ListBookingOrders, user);
    const shouldShowAdvancedSearch = Features.hasFeature(Features.ListAdvanceSearchOrders, user);
    const shouldShowCreateGenericOrder = Features.hasFeature(Features.CreateGenericOrder, user);
    const shouldShowDivider = Features.hasAnyFeature(
        [Features.ListAdvanceSearchOrders, Features.CreateGenericOrder],
        user
    );

    function handlePlanOrdersClick() {
        onClose();
        history.push(PlanListRoute);
    }

    function handlePhotoOrdersClick() {
        onClose();
        history.push(PhotoListRoute);
    }

    function handleFilmOrdersClick() {
        onClose();
        history.push(FilmListRoute);
    }

    function handleDigitalStylingOrdersClick() {
        onClose();
        history.push(DigitalStylingListRoute);
    }

    function handleBookingOrdersClick() {
        onClose();
        history.push(BookingsListRoute);
    }

    function handleAdvancedSearchOrdersClick() {
        onClose();
        history.push(AdvancedSearchRoute);
    }

    function handleCreateGenericOrdersClick() {
        onClickCreateGenericOrder();
        onClose();
    }

    return (
        <Menu anchorEl={anchorElement} open={open} onClose={onClose}>
            {shouldShowPlanOrders ? (
                <BaseMenuItem
                    route={PlanListRoute}
                    onClick={handlePlanOrdersClick}
                    label={labels.listPlanOrders}
                />
            ) : null}
            {shouldShowPhotoOrders ? (
                <BaseMenuItem
                    route={PhotoListRoute}
                    onClick={handlePhotoOrdersClick}
                    label={labels.listPhotoOrders}
                />
            ) : null}
            {shouldShowFilmOrders ? (
                <BaseMenuItem
                    route={FilmListRoute}
                    onClick={handleFilmOrdersClick}
                    label={labels.listFilmOrders}
                />
            ) : null}
            {shouldShowDigitalStylingOrders ? (
                <BaseMenuItem
                    route={DigitalStylingListRoute}
                    onClick={handleDigitalStylingOrdersClick}
                    label={labels.listDigitalStylingOrders}
                />
            ) : null}
            {shouldShowBookingOrders ? (
                <BaseMenuItem
                    route={BookingsListRoute}
                    onClick={handleBookingOrdersClick}
                    label={labels.listBookingOrders}
                />
            ) : null}
            {shouldShowDivider ? <Divider /> : null}
            {shouldShowAdvancedSearch ? (
                <BaseMenuItem
                    route={AdvancedSearchRoute}
                    onClick={handleAdvancedSearchOrdersClick}
                    label={labels.advancedSearch}
                />
            ) : null}
            {shouldShowCreateGenericOrder ? (
                <StyledMenuItem onClick={handleCreateGenericOrdersClick}>
                    {labels.createGenericOrder}
                </StyledMenuItem>
            ) : null}
        </Menu>
    );
}

function TranslatedAdminOrderMenu(props: ExternalProps) {
    const labels = {
        listPlanOrders: Translate('header/nav/orders-list/plan'),
        listPhotoOrders: Translate('header/nav/orders-list/photo'),
        listFilmOrders: Translate('header/nav/orders-list/film'),
        listDigitalStylingOrders: Translate('header/nav/orders-list/digital-styling'),
        listBookingOrders: Translate('header/nav/orders-list/booking'),
        advancedSearch: Translate('header/nav/orders-list/advance'),
        createGenericOrder: Translate('view-order-collection/create-new-generic-order/button')
    };
    return <AdminOrderMenu {...props} labels={labels} />;
}

function AdminOrderTab({ label, ...props }: AdminOrderTabProps) {
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
    const [showCreateGenericOrderModal, setShowCreateGenericOrderModal] = useState<boolean>(false);

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        setAnchorElement(event.currentTarget);
        setShowMenu((oldShowMenu) => !oldShowMenu);
    }

    function handleClickCreateGenericOrder() {
        setShowCreateGenericOrderModal(true);
    }

    function handleCloseCreateGenericOrderModal() {
        setShowCreateGenericOrderModal(false);
    }

    function handleCloseCreateGenericOrderModalAndNavigate(path: string) {
        setShowCreateGenericOrderModal(false);
        history.push(path);
    }

    function handleCloseMenu() {
        setShowMenu(false);
    }

    return (
        <>
            <BaseTab
                {...props}
                icon={
                    showMenu ? (
                        <ExpandLessIcon sx={{ width: '24px', height: '24px' }} />
                    ) : (
                        <ExpandMoreIcon sx={{ width: '24px', height: '24px' }} />
                    )
                }
                iconPosition="end"
                onClick={handleClick}
                label={label}
                sx={{ minHeight: '48px', height: '48px' }}
                route={null}
            />
            <TranslatedAdminOrderMenu
                open={showMenu}
                anchorElement={anchorElement}
                onClose={handleCloseMenu}
                onClickCreateGenericOrder={handleClickCreateGenericOrder}
            />
            <CreateGenericOrderModal
                showModal={showCreateGenericOrderModal}
                closeModal={handleCloseCreateGenericOrderModal}
                closeAndNavigate={handleCloseCreateGenericOrderModalAndNavigate}
            />
        </>
    );
}

function TranslatedAdminOrderTab(props) {
    const label = Translate('header/nav/orders-list');
    return <AdminOrderTab {...props} label={label} />;
}

TranslatedAdminOrderTab.routes = routes;

export default TranslatedAdminOrderTab;
